import { Formik } from "formik";
import Button from "../../../components/form-control/Button";
import Page from '../../../components/PageWrapper';
import Input from "../../../components/form-control/Input";
import * as Yup from 'yup';
import { Save, Shield } from "lucide-react";
import { useState } from "react";
import { SESSION_PERIOD } from "../../../resources/enums";
import { IChangePasswordDTO } from "../../../resources/interfaces";
import { changeStaffPassword } from "../../../api/requests";
import useUserAccount from "../../../hooks/userAccountHook";
import { showToast } from "../../../utils/miscFunctions";

const SecuritySettings = () => {
  const { signOut } = useUserAccount();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isPasswordFormDirty, setIsPasswordFormDirty] = useState(false);
  const [securityData, setSecurityData] = useState({
    twoFactorEnabled: false,
    sessionTimeout: '30',
    loginNotifications: true
  });

  const SessionPeriodList = Object.values(SESSION_PERIOD).map(period => {
    return period
  });

  const handleEnableTwoFactor = (e: React.MouseEvent) => {
    e.preventDefault();
    showToast.info('Two factor authentication implementation is in progress');
  }

  return (
    <Page title="Security" className="">
      <div className="py-6 bg-white dark:bg-darkBg min-h-full">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Security Recommendations</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Use a strong password with at least 8 characters, including uppercase, lowercase, numbers, and symbols.
              </p>
            </div>
          </div>
        </div>

        <Formik
          initialValues={{
            password: '',
            newPassword: '',
            confirmNewPassword: '',
          }}
          validateOnBlur
          validationSchema={isPasswordFormDirty ? Yup.object().shape({
            password: Yup.string().required('Current password is required'),
            newPassword: Yup.string()
              .required('New password is required')
              .min(8, 'Password must be at least 8 characters')
              .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).+$/,
                'Must include uppercase, lowercase, number, and special character'
              ),
            confirmNewPassword: Yup.string()
              .oneOf([Yup.ref('newPassword')], 'Passwords must match')
              .required('Confirm new password is required')
          }) : Yup.object()}
          onSubmit={async (values, { resetForm }) => {

            const changePasswordDto: IChangePasswordDTO = {
              currentPassword: values.password,
              newPassword: values.newPassword
            }

            try {
              const response = await changeStaffPassword(changePasswordDto)
              if (response) {
                showToast.success(response.message ?? 'Password changed successfully');
                setUnsavedChanges(false);
                setIsPasswordFormDirty(false);
                resetForm();
                signOut();
              }
            } catch (error: any) {
              showToast.error(error.description);
            }

          }}>
          {({ values, errors, touched, handleSubmit, handleChange, isSubmitting, setFieldTouched, setFieldValue }) => (

            <form className="md:px-[45px] py-[30px] bg-white dark:bg-darkBg min-h-full" onSubmit={handleSubmit}>

              <div className="space-y-4">
                <div className="relative">
                  <Input
                    label='Current Password'
                    type='password'
                    name='password'
                    id="password"
                    value={values.password}
                    onBlur={(e) => {
                      handleChange(e);
                      setFieldTouched('password', true, true);
                    }}
                    onChange={(e) => {
                      handleChange(e);
                      if (!isPasswordFormDirty || values.password.length > 0) {
                        setIsPasswordFormDirty(true);
                      }
                      setUnsavedChanges(true);
                    }}
                    required
                    error={touched.password ? errors.password : undefined}
                    helperText={touched.password ? errors.password : undefined}
                    showPasswordIcon
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label='New Password'
                    type='password'
                    name='newPassword'
                    id="newPassword"
                    value={values.newPassword}
                    onBlur={(e) => {
                      handleChange(e);
                      setFieldTouched('newPassword', true, true);
                    }}
                    onChange={(e) => {
                      handleChange(e);
                      if (!isPasswordFormDirty) {
                        setIsPasswordFormDirty(true);
                      }
                      setUnsavedChanges(true);
                    }}
                    required
                    error={touched.newPassword ? errors.newPassword : undefined}
                    helperText={touched.newPassword ? errors.newPassword : undefined}
                    showPasswordIcon
                  />

                  <Input
                    label='Confirm New Password'
                    type='password'
                    name='confirmNewPassword'
                    id="confirmNewPassword"
                    value={values.confirmNewPassword}
                    onBlur={(e) => {
                      handleChange(e);
                      setFieldTouched('confirmNewPassword', true, true);
                    }}
                    onChange={(e) => {
                      handleChange(e);
                      if (!isPasswordFormDirty) {
                        setIsPasswordFormDirty(true);
                      }
                      setUnsavedChanges(true);
                    }}
                    required
                    error={touched.confirmNewPassword ? errors.confirmNewPassword : undefined}
                    helperText={touched.confirmNewPassword ? errors.confirmNewPassword : undefined}
                    showPasswordIcon
                  />
                </div>
              </div>

              {/* Two Factor Authentication */}
              <div className="pt-6 mb-1">
                {/* <div className="pt-6 mb-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-4">Two-Factor Authentication</h3>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-darkBg-border rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">Authenticator App</h4>
                      <p className="text-sm text-gray-500 dark:text-darkBg-subText">Use an authenticator app to generate verification codes</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${securityData.twoFactorEnabled ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'}`}>
                        {securityData.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        type="button"
                        onClick={handleEnableTwoFactor}
                        // onClick={() => setSecurityData({ ...securityData, twoFactorEnabled: !securityData.twoFactorEnabled })}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${securityData.twoFactorEnabled ? 'bg-gray-600 dark:bg-gray-500' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out ${securityData.twoFactorEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </button>
                    </div>
                  </div>
                </div> */}
              </div>

              {/* Session Management */}
              {/* 
              <div className=" py-6 mt-6 flex flex-col w-fit ">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Session Management</h3>

                <SelectMenu
                  onChange={(option: string | null) => setFieldValue('sessionPeriod', option)}
                  listSelectedValue={values.sessionPeriod}
                  name="sessionPeriod"
                  label="Session Timeout (minutes)"
                  list={SessionPeriodList}
                  required
                  error={touched.sessionPeriod ? errors.sessionPeriod : undefined}
                  helperText={touched.sessionPeriod ? errors.sessionPeriod : undefined}
                />
              </div> 
              */}

              <div className="px-6 py-4 bg-gray-50 dark:bg-darkBg-border border-t border-gray-200 dark:border-darkBg-border flex items-center justify-between rounded-lg">
                <div className="flex items-center space-x-2">
                  {unsavedChanges && (
                    <span className="text-sm text-yellow-600 dark:text-yellow-400">
                      {isPasswordFormDirty ? 'You have unsaved password changes' : 'You have unsaved changes'}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <Button
                    label="Save"
                    type="submit"
                    bgColor="primary"
                    disabled={isSubmitting || (!isPasswordFormDirty && !securityData.twoFactorEnabled)}
                    loading={isSubmitting}
                    icon={<Save className="mr-2 h-4 w-4" />}
                  />
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </Page>
  )
};

export default SecuritySettings;
